import React, { FC, useEffect, useState } from "react";

import { useForm, useWatch } from "react-hook-form";
import { useSiteCore } from "../../../../hooks/useSiteCore";

import {
  ChipOptionsDataType,
  FormController,
  MuiBox,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  RowProps,
} from "../../../../components";
import MuiChipWithPopover from "../../../../components/chipWithPopover/MuiChipWithPopover";

import { useGetCountryListForDropdowns } from "../../../../api/services/client/clientApis";
import { IdNameList } from "../../../../api/services/common/models";
import MuiCustomMenu from "../../../../components/customMenu/MuiCustomMenu";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import { UnitTypesKey } from "../../../../helpers/constants/siteCoreKeys";
import { LabelValue } from "../../../../helpers/types/common";
import {
  arrayOfObjectsIncludesValue,
  arrayOfStringsIncludesValue,
  checkFilterMatch,
  createDropdownLabelResolver,
  getCountryNameByCode,
  getUniqueFilters,
  getUniqueLabelOptions,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { TopicListItem } from "../../../../redux/slices/mdmActivity/models";
import {
  MdmActivityTopicResultKey,
  MdmActivityTopicRowValue,
  preferencesKey,
  UniqueTopicResultFiltersTypes,
} from "../../modelsAndSchemas/models";
import { mdmAddActivityTopicSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

interface ActivityTopicListProps {
  rows: TopicListItem[];
  pageId: string;
  handleDeleteTopic?: (id: string) => void;
  isViewPage?: boolean;
  handleViewTopic?: (id: string) => void;
  handleEditTopic?: (id: string) => void;
}

export const ActivityTopicList: FC<ActivityTopicListProps> = ({
  rows,
  pageId,
  handleDeleteTopic,
  isViewPage = false,
  handleViewTopic,
  handleEditTopic,
}) => {
  const tableRef = React.useRef<{ resetPagination: () => void }>(null);

  const [filteredResults, setFilteredResults] = useState<
    MdmActivityTopicRowValue[]
  >([]);

  const [uniqueFilters, setUniqueFilters] =
    useState<UniqueTopicResultFiltersTypes>(() => ({
      preferences: [],
      activityStatus: [],
      participantStatus: [],
    }));
  const [allUpdatedRows, setAllUpdatedRows] =
    useState<MdmActivityTopicRowValue[]>();

  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();

  const getTopicLabel = (key: string) => {
    return fetchLabel(mdmAddActivityTopicSiteCoreKeys.topicListKey, key);
  };

  const getMeasurementLabelByUnit = (unit: string): string => {
    if (unit === UnitTypesKey.Minutes) {
      return getTopicLabel(mdmAddActivityTopicSiteCoreKeys.minutesLabel);
    }

    const otherKey =
      unit === UnitTypesKey.Count
        ? mdmAddActivityTopicSiteCoreKeys.quantityLabel
        : mdmAddActivityTopicSiteCoreKeys.numberOfDaysLabel;

    return getTopicLabel(otherKey);
  };

  const { data: countryListData } = useGetCountryListForDropdowns();

  const { fetchLanguageDropdownValues, commonARIALabel } =
    useNavigationSiteCore();

  const { control, reset, getValues } = useForm({
    mode: "all",
    defaultValues: {
      [MdmActivityTopicResultKey.topicName]: "",
      [MdmActivityTopicResultKey.deliverables]: "",
      [MdmActivityTopicResultKey.activityStatus]: "",
      [MdmActivityTopicResultKey.participantStatus]: "",
      [MdmActivityTopicResultKey.preference]: "",
    },
  });

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    resetPagination();
    reset();
  }, [rows]);

  useEffect(() => {
    const siteCoreLanguages = fetchLanguageDropdownValues();

    const localeMap = new Map(
      siteCoreLanguages.map(({ value, label }) => [value, label]),
    );

    const localRows = structuredClone(rows);

    const mappedResults = localRows.map((item) =>
      mapRowToTopicResult(item, localeMap),
    );

    setAllUpdatedRows(mappedResults);
    setFilteredResults(mappedResults);

    const uniquePreferences = extractUniquePreferences(mappedResults);
    const uniqueActivityStatuses = getUniqueFilters(
      mappedResults,
      "activityStatus",
      "activityStatusLabel",
    );

    const uniqueParticipantStatuses = getUniqueLabelOptions(
      mappedResults,
      "participantStatusLabel",
    );

    setUniqueFilters((prevFilters) => ({
      ...prevFilters,
      preferences: uniquePreferences,
      activityStatus: uniqueActivityStatuses,
      participantStatus: uniqueParticipantStatuses,
    }));
  }, [rows, countryListData]);

  const getPreferences = (item: TopicListItem): IdNameList[] => {
    return [
      item.triggerBilling && {
        id: preferencesKey.triggerBilling,
        name: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.triggerBilling),
      },
      item.countsTowardsLastContactDate && {
        id: preferencesKey.countsTowardsLastContactDate,
        name: getTopicLabel(
          mdmAddActivityTopicSiteCoreKeys.countTowardsLastContactDate,
        ),
      },
      item.triggerTouchPointSurvey && {
        id: preferencesKey.triggerTouchPointSurvey,
        name: getTopicLabel(
          mdmAddActivityTopicSiteCoreKeys.triggerTouchPointSurvey,
        ),
      },
    ].filter(Boolean) as IdNameList[];
  };

  const getLocalizedTopics = (
    locales: TopicListItem["localizedTopics"],
    localeMap: Map<string, string>,
  ) =>
    locales?.map((locale) => ({
      value: locale.name,
      name: localeMap.get(locale.localeKey) ?? locale.localeKey,
    })) ?? [];

  const getDefaultValueExceptions = (
    exceptions: TopicListItem["defaultValueExceptions"],
  ) =>
    exceptions?.map((ex) => ({
      name: getCountryNameByCode(ex.country ?? "", countryListData ?? []),
      value:
        ex.units !== UnitTypesKey.HalfDay
          ? `${ex.measurementName} ${getMeasurementLabelByUnit(ex.units)}`
          : "",
    })) ?? [];

  const resolveParticipantValueForDropdown = createDropdownLabelResolver(
    mdmAddActivityTopicSiteCoreKeys.topicListKey,
    mdmAddActivityTopicSiteCoreKeys.participantStatusList,
    fetchValueByKeyOfDropdown,
  );

  const mapRowToTopicResult = (
    item: TopicListItem,
    localeMap: Map<string, string>,
  ): MdmActivityTopicRowValue => ({
    id: item.id,
    topicName: item.topic,
    deliverables: item.deliverables,
    activityStatus: item.activityStatus,
    activityStatusLabel: fetchValueByKeyOfDropdown(
      mdmAddActivityTopicSiteCoreKeys.topicListKey,
      mdmAddActivityTopicSiteCoreKeys.activityStatusList,
      item.activityStatus,
    ),
    participantStatus: item.candidateStatus,
    participantStatusLabel: resolveParticipantValueForDropdown(
      item.candidateStatus,
    ),
    preference: getPreferences(item),
    unit: item.unit,
    unitName: fetchValueByKeyOfDropdown(
      mdmAddActivityTopicSiteCoreKeys.topicListKey,
      mdmAddActivityTopicSiteCoreKeys.unitList,
      item.unit,
    ),
    measurement:
      item.unit === UnitTypesKey.Minutes
        ? fetchValueByKeyOfDropdown(
            mdmAddActivityTopicSiteCoreKeys.topicListKey,
            mdmAddActivityTopicSiteCoreKeys.minutesList,
            item.measurement,
          )
        : item.measurement,
    localizedTopics: getLocalizedTopics(item.localizedTopics, localeMap),
    defaultValueExceptions: getDefaultValueExceptions(
      item.defaultValueExceptions,
    ),
  });

  const extractUniquePreferences = (
    rows: MdmActivityTopicRowValue[],
  ): LabelValue[] => {
    const preferenceMap = new Map<string, string>();
    rows.forEach((row) => {
      row.preference?.forEach(({ id, name }) => {
        if (!preferenceMap.has(id)) preferenceMap.set(id, name);
      });
    });
    return Array.from(preferenceMap, ([value, label]) => ({ value, label }));
  };

  const filters = useWatch({ control });

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        topicName,
        deliverables,
        activityStatus,
        participantStatus,
        preference,
      } = getValues();

      const filteredData =
        allUpdatedRows?.filter((item) => {
          const topicNameMatch = checkFilterMatch(topicName, item.topicName);

          const deliverablesMatch = arrayOfObjectsIncludesValue(
            deliverables,
            item.deliverables,
            "name",
          );

          const activityStatusMatch = checkFilterMatch(
            activityStatus,
            item.activityStatus,
          );

          const participantStatusMatch = arrayOfStringsIncludesValue(
            participantStatus,
            item.participantStatusLabel,
          );

          const preferenceMatch = arrayOfObjectsIncludesValue(
            preference,
            item.preference,
            "id",
          );

          return (
            topicNameMatch &&
            deliverablesMatch &&
            activityStatusMatch &&
            participantStatusMatch &&
            preferenceMatch
          );
        }) ?? [];
      setFilteredResults([...filteredData]);
      resetPagination();
    }
  }, [filters]);

  const columns = [
    {
      id: MdmActivityTopicResultKey.topicName,
      label: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.topicColumn),
      isSortVisible: true,
      className: "cell-topic",
      ...(isViewPage && {
        render: (row: RowProps) =>
          !row.topicName ? null : (
            <MuiLink
              onClick={() => handleViewTopic?.(row.id)}
              id={`${pageId}-view-topic-${row.id}`}
              data-testid="view-topic-link"
            >
              <MuiBox>
                <MuiEllipseLabel
                  labelTypoClassName="Link"
                  text={row.topicName}
                  labelTypoVariant="h3"
                  placement="bottom-start"
                  pointerCursor
                />
              </MuiBox>
            </MuiLink>
          ),
      }),
    },
    {
      id: MdmActivityTopicResultKey.deliverables,
      label: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.deliverables),
      isSortVisible: true,
      sortKeyName: "name",
      objectKeyName: MdmActivityTopicResultKey.deliverables,
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={
            row.deliverables?.map((item: IdNameList) => ({
              label: item.name,
              value: item.id,
            })) as ChipOptionsDataType[]
          }
          id={"selected-deliverables"}
          isEditable={false}
          headerLabel={getTopicLabel(
            mdmAddActivityTopicSiteCoreKeys.selectedDeliverables,
          )}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: MdmActivityTopicResultKey.unitName,
      label: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.units),
      isSortVisible: true,
    },
    {
      id: MdmActivityTopicResultKey.measurement,
      label: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.measurement),
      isSortVisible: true,
    },
    {
      id: MdmActivityTopicResultKey.activityStatus,
      label: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.activityStatus),
      isSortVisible: true,
      className: "cell-status",
      render: (row: any) => (
        <StatusBadge
          status={row.activityStatus}
          label={row.activityStatusLabel}
        />
      ),
    },
    {
      id: MdmActivityTopicResultKey.participantStatus,
      label: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.participantStatus),
      isSortVisible: true,
      className: "cell-candidate-status",
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={row.participantStatus?.map((item: string) => ({
            label: fetchValueByKeyOfDropdown(
              mdmAddActivityTopicSiteCoreKeys.topicListKey,
              mdmAddActivityTopicSiteCoreKeys.participantStatusList,
              item,
            ),
            value: item,
          }))}
          id={"selected-candidate-status"}
          isEditable={false}
          headerLabel={getTopicLabel(
            mdmAddActivityTopicSiteCoreKeys.participantStatus,
          )}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: MdmActivityTopicResultKey.preference,
      label: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.preferences),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={
            row.preference?.map((item: IdNameList) => ({
              label: item.name,
              value: item.id,
            })) as ChipOptionsDataType[]
          }
          id={"selected-preference"}
          isEditable={false}
          headerLabel={getTopicLabel(
            mdmAddActivityTopicSiteCoreKeys.selectedPreferences,
          )}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: MdmActivityTopicResultKey.locale,
      label: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.localeLabel),
      isSortVisible: false,
      className: "cell-status",
      render: (row: RowProps) =>
        isNotEmpty(row.localizedTopics) ? (
          <MuiCustomMenu
            data={{
              title: `${getTopicLabel(mdmAddActivityTopicSiteCoreKeys.localeLabel)} - ${row.topicName}`,
              data: row.localizedTopics,
            }}
            isLocale
          />
        ) : (
          <></>
        ),
    },
    {
      id: MdmActivityTopicResultKey.exceptions,
      label: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.exceptions),
      isSortVisible: false,
      className: "cell-status",
      render: (row: RowProps) =>
        isNotEmpty(row.defaultValueExceptions) ? (
          <MuiCustomMenu
            data={{
              title: `${getTopicLabel(mdmAddActivityTopicSiteCoreKeys.exceptions)} - ${row.topicName}`,
              data: row.defaultValueExceptions,
            }}
            isLocale
          />
        ) : (
          <></>
        ),
    },
    ...(!isViewPage
      ? [
          {
            id: "action",
            label: getTopicLabel(mdmAddActivityTopicSiteCoreKeys.actions),
            render: (row: RowProps) => {
              return (
                <MuiOptionMenu
                  menuItems={[
                    {
                      onClick: () => handleEditTopic?.(row.id),
                      dataTestId: `edit`,
                      label: commonLabels.edit,
                      ariaLabel: commonLabels.edit,
                    },
                    // Commenting out temporary on basis of Story requirement 94717
                    // {
                    //   onClick: () => handleDeleteTopic?.(row.id),
                    //   dataTestId: `delete`,
                    //   label: commonLabels.delete,
                    //   ariaLabel: commonLabels.delete,
                    // },
                  ]}
                />
              );
            },
          },
        ]
      : []),
  ];

  const tableHeaderForm = [
    <FormController
      control={control}
      name={MdmActivityTopicResultKey.topicName}
      key={MdmActivityTopicResultKey.topicName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${MdmActivityTopicResultKey.topicName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      name={MdmActivityTopicResultKey.deliverables}
      key={MdmActivityTopicResultKey.deliverables}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${MdmActivityTopicResultKey.deliverables}`}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <></>,
    <></>,
    <FormController
      control={control}
      key={MdmActivityTopicResultKey.activityStatus}
      name={MdmActivityTopicResultKey.activityStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.activityStatus}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${MdmActivityTopicResultKey.activityStatus}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={MdmActivityTopicResultKey.participantStatus}
      name={MdmActivityTopicResultKey.participantStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.participantStatus}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${MdmActivityTopicResultKey.participantStatus}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={MdmActivityTopicResultKey.preference}
      name={MdmActivityTopicResultKey.preference}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.preferences}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${MdmActivityTopicResultKey.preference}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    <></>,
    ...(!isViewPage ? [<></>] : []),
  ];

  return (
    <MuiBox data-testid={`${pageId}-activity-topic-list`}>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        tableResults={[...filteredResults]}
        noDataInApi={rows.length === 0}
        emptyMessage={getTopicLabel(
          mdmAddActivityTopicSiteCoreKeys.noTopicsAvailableMessage,
        )}
        noResultMessage={commonLabels.noResult}
        priorityResults={[]}
        isLastColumnSticky={!isViewPage}
      />
    </MuiBox>
  );
};
